import { memo, useMemo } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { environment } from 'apps/table-tuck-web/src/environments/environment';

/**
 * Address section showing delivery address or pickup location
 * Memoized to prevent unnecessary re-renders
 */
const AddressSection = memo(
  ({ orderType, currentStore, currentAddress, onChangeAddress }) => {

    // Format store address
    const storeAddress = useMemo(() => {
      if (!currentStore?.address) return '';

      const addr = currentStore.address;
      if (typeof addr === 'string') return addr;

      const parts = [];
      if (addr.addressLine1) parts.push(addr.addressLine1);
      if (addr.city || addr.locality) {
        const cityState = [
          addr.city || addr.locality,
          addr.state || addr.administrativeDistrictLevel1,
        ]
          .filter(Boolean)
          .join(', ');
        parts.push(cityState);
      }
      return parts.join(', ');
    }, [currentStore]);

    const displayAddress = useMemo(() => {
      if (orderType === 'delivery') {
        return (
          currentAddress?.displayName ||
          currentAddress?.address ||
          'No address selected'
        );
      }
      return storeAddress;
    }, [orderType, currentAddress, storeAddress]);

    return (
      <Box
        sx={{
          width: '100%',
          padding: '15px 5%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#FFF4F0',
            marginBottom: '8px',
          }}
        >
          {orderType === 'delivery' ? (
            <LocalShippingIcon
              style={{
                color: environment.mainColor,
                fontSize: '28px',
              }}
            />
          ) : (
            <StorefrontIcon
              style={{
                color: environment.mainColor,
                fontSize: '28px',
              }}
            />
          )}
        </Box>

        {/* Pickup from / Deliver to label */}
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            color: '#808080',
            fontSize: '13px',
            fontWeight: '500',
          }}
        >
          {orderType === 'delivery' ? 'Deliver to' : 'Pickup from'}
        </Typography>

        {/* Address Text */}
        <Box sx={{ textAlign: 'center' }}>
          {orderType === 'delivery' ? (
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Jost, sans-serif',
                color: '#172B4D',
                fontWeight: '600',
                fontSize: '16px',
                lineHeight: '1.4',
                maxWidth: '280px',
              }}
            >
              {displayAddress}
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  color: '#172B4D',
                  fontWeight: '600',
                  fontSize: '16px',
                }}
              >
                {currentStore?.businessName || currentStore?.name || ''}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  color: '#172B4D',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                {storeAddress}
              </Typography>
            </Stack>
          )}
        </Box>

        {/* Change Button for Delivery */}
        {orderType === 'delivery' && (
          <Stack spacing={0.5}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Jost, sans-serif',
                color: '#172B4D',
                fontWeight: '600',
                fontSize: '16px',
              }}
            >
              {currentStore?.businessName || currentStore?.name || ''}
            </Typography>
            <Typography
              component="button"
              onClick={onChangeAddress}
              sx={{
                fontFamily: 'Jost, sans-serif',
                color: environment.mainColor,
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                border: 'none',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              Change Address
            </Typography>
          </Stack>
        )}
      </Box>
    );
  }
);

AddressSection.displayName = 'AddressSection';

export default AddressSection;
